import { Link } from "react-router-dom";
import { Lock, PawPrint, ShieldCheck } from "lucide-react";

const LEGAL_LINKS = [
  { to: "/legal/privacy", label: "Privacy Policy", testid: "footer-link-privacy" },
  { to: "/legal/terms", label: "Terms of Service", testid: "footer-link-terms" },
  { to: "/legal/security", label: "Security & Compliance", testid: "footer-link-security" },
  { to: "/legal/cookies", label: "Cookie Notice", testid: "footer-link-cookies" },
];

const YEAR = new Date().getFullYear();

// Compact single-row footer for in-app pages and auth screens.
export const LegalFooterCompact = () => (
  <footer className="border-t border-border py-4 mt-8" data-testid="legal-footer-compact">
    <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
      <p className="text-[11px] text-muted-foreground">© {YEAR} Soula Paws. Not a veterinary provider — for emergencies contact a vet or emergency clinic.</p>
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {LEGAL_LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="text-[11px] text-muted-foreground hover:text-foreground hover:underline transition-colors duration-150" data-testid={`${l.testid}-compact`}>
            {l.label}
          </Link>
        ))}
      </nav>
    </div>
  </footer>
);

// Full footer for public pages (landing, legal pages).
export const LegalFooter = () => (
  <footer className="border-t border-border bg-secondary/30" data-testid="legal-footer">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center"><PawPrint className="w-3.5 h-3.5 text-primary-foreground" /></div>
            <span className="font-display text-base font-semibold">Soula Paws</span>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed max-w-sm">
            The privacy-first AI bond and wellness OS for people and their animals. Observation-based wellness support only — Soula Paws is not a veterinary provider, does not diagnose or prescribe, and never replaces professional veterinary care. For emergencies, contact a veterinarian or emergency clinic immediately.
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Legal</p>
          <nav className="space-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="block text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors duration-150" data-testid={l.testid}>
                {l.label}
              </Link>
            ))}
            <Link to="/download" className="block text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors duration-150" data-testid="footer-link-download">
              Get the mobile app
            </Link>
          </nav>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Trust & Security</p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span className="text-sm text-muted-foreground">Security program modeled on SOC 2 Trust Services Criteria</span></div>
            <div className="flex items-start gap-2"><Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span className="text-sm text-muted-foreground">Data encrypted in transit · Payments secured by Stripe (PCI DSS)</span></div>
            <p className="text-sm text-muted-foreground">Questions? <a href="mailto:luminasoula@gmail.com" className="text-primary hover:underline" data-testid="footer-contact-email">luminasoula@gmail.com</a></p>
          </div>
        </div>
      </div>
      <div className="border-t border-border mt-8 pt-5 flex flex-col md:flex-row items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">© {YEAR} Soula Paws. All rights reserved.</p>
        <p className="text-[11px] text-muted-foreground text-center">HIPAA-grade safeguards applied to sensitive data · We never sell your personal information.</p>
      </div>
    </div>
  </footer>
);
